import { useState, type ComponentType, type ReactNode, type SVGProps } from "react";
import { CalendarMinus, CalendarX2, EyeOff, Globe, Layers } from "lucide-react";
import type { CalendarPlanSlot, CalendarPlatform } from "@/types/contentCalendar.type";
import { isDirectSlot } from "@/types/contentCalendar.type";
import type { BriefRailInfo } from "@/types/productionBrief.type";
import { clientTitle } from "../postState";
import { slotTitle } from "../slotStage";
import { CHANNEL_NAME } from "../month/calendarModel";
import { AcknowledgeRow, CalendarConfirm } from "../month/CalendarSheet";

type Icon = ComponentType<SVGProps<SVGSVGElement> & { strokeWidth?: number }>;

function listOf(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

function platforms(list: { platform: CalendarPlatform }[]): string {
  return listOf([...new Set(list.map((p) => CHANNEL_NAME[p.platform] ?? p.platform))]);
}

/** Dialog consequence row: one line of what goes, after a 16px icon (kept for the re-plan confirm). */
export function Consequence({ icon: IconPart, children }: { icon: Icon; children: ReactNode }) {
  return (
    <li className="flex items-start gap-2.5 text-[14px] leading-[1.45] text-co-text2">
      <IconPart className="mt-0.5 size-4 shrink-0" strokeWidth={1.75} aria-hidden="true" />
      <span className="min-w-0">{children}</span>
    </li>
  );
}

/**
 * Confirm deleting one post (DESIGN.md "Every post can be deleted"; Fea6g,
 * epW6O, zFUPa). Dialog / Confirm: the question, the post leaving the calendar,
 * then what goes with it, counted from what it actually carries (its ideas and
 * their comments, the creative brief, the approved files, any publication
 * already scheduled or live), whether the client can see it now, and the
 * month's count after it. It cannot be undone, and it says so. A post with
 * scheduled or live publications asks for a tick first, as it always has.
 */
export default function DeleteSlotModal({
  slot,
  loading,
  onConfirm,
  onClose,
  brief = null,
  clientName = null,
  phone = false,
  monthAfter = null,
}: {
  slot: CalendarPlanSlot;
  loading: boolean;
  onConfirm: () => void;
  onClose: () => void;
  /** The post's creative brief, when the host has read it. */
  brief?: BriefRailInfo | null;
  clientName?: string | null;
  /** On a phone the dialog is a sheet floating 8px in. */
  phone?: boolean;
  /** The month after the delete, when the host knows it: "December drops to 4 posts: static 0 of 1." */
  monthAfter?: string | null;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const title = slotTitle(slot);
  const pubs = slot.deliverable?.publications ?? [];
  const scheduled = pubs.filter((p) => p.status === "SCHEDULED" || p.status === "PUBLISHING");
  const published = pubs.filter((p) => p.status === "PUBLISHED");
  const ideas = slot.ideas ?? [];
  const comments = ideas.reduce((n, idea) => n + (idea.comments?.length ?? 0), 0);
  const inReview = slot.status === "IN_CLIENT_REVIEW";
  const highStakes = scheduled.length > 0 || published.length > 0;
  const client = clientTitle(clientName);
  const canSubmit = !highStakes || confirmed;

  // "Its ideas, comments and brief go with it.": one line for the work on it.
  const work = [
    ideas.length > 0 ? (ideas.length === 1 ? "idea" : "ideas") : null,
    comments > 0 ? (comments === 1 ? "comment" : "comments") : null,
    brief ? "brief" : null,
    slot.deliverable ? (isDirectSlot(slot) ? "files" : "approved files") : null,
  ].filter((part): part is string => !!part);

  const items: { key: string; icon: Icon; text: string }[] = [];
  if (work.length > 0) items.push({ key: "work", icon: Layers, text: `Its ${listOf(work)} ${work.length === 1 && !work[0].endsWith("s") ? "goes" : "go"} with it.` });
  if (inReview) items.push({ key: "client", icon: EyeOff, text: `${client} can see it now. It leaves their page.` });
  if (scheduled.length > 0) {
    items.push({
      key: "scheduled",
      icon: CalendarX2,
      text: `${scheduled.length === 1 ? "The post scheduled" : "The posts scheduled"} on ${platforms(scheduled)}. ${scheduled.length === 1 ? "It does" : "They do"} not go out.`,
    });
  }
  if (published.length > 0) {
    items.push({
      key: "published",
      icon: Globe,
      text:
        new Set(published.map((p) => p.platform)).size > 1
          ? `It already went live on ${platforms(published)}. Those posts stay up there; take them down on each platform.`
          : `It already went live on ${platforms(published)}. That stays up there; take it down on the platform.`,
    });
  }
  if (monthAfter) items.push({ key: "month", icon: CalendarMinus, text: monthAfter });

  return (
    <CalendarConfirm
      phone={phone}
      title="Delete this post?"
      description={`“${title}” leaves the calendar.`}
      onClose={onClose}
      busy={loading}
      cancelLabel="Keep post"
      confirmLabel={loading ? "Deleting…" : "Delete post"}
      onConfirm={onConfirm}
      confirmDisabled={!canSubmit}
    >
      {items.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {items.map((item) => (
            <Consequence key={item.key} icon={item.icon}>
              {item.text}
            </Consequence>
          ))}
        </ul>
      ) : null}
      <p className="text-[13px] font-semibold text-co-alert-ink">This cannot be undone.</p>
      {highStakes ? (
        <AcknowledgeRow checked={confirmed} onChange={setConfirmed} disabled={loading}>
          I understand this post is deleted{scheduled.length > 0 ? " and its schedule cancelled" : ""}.
        </AcknowledgeRow>
      ) : null}
    </CalendarConfirm>
  );
}
